import React, { useState, useEffect } from "react";
import {
  X,
  Receipt,
  Edit2,
  Download,
  Building,
  CreditCard,
  Briefcase,
  Info,
  Maximize2,
  FileText,
} from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../../App";
import { useTranslation } from "react-i18next";

export default function BossModalShowInvoice({
  visible,
  onClose,
  invoice,
  expense,
}) {
  const { t } = useTranslation();
  const [allData, setAllData] = useState(null);
  const [userData, setUserData] = useState(null);

  const [isExporting, setIsExporting] = useState(false);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Form states
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [businessNumber, setBusinessNumber] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [subTotal, setSubTotal] = useState("");
  const [taxRate, setTaxRate] = useState("");
  const [taxAmount, setTaxAmount] = useState("");
  const [totalAmount, setTotalAmount] = useState("");

  // Load from localStorage
  useEffect(() => {
    const data = localStorage.getItem("allData");
    const user = localStorage.getItem("user");
    if (data) setAllData(JSON.parse(data));
    if (user) setUserData(JSON.parse(user));
  }, []);

  const linkedExpense =
    expense ||
    allData?.myExpenses?.find((exp) => exp.id === invoice?.expensesId);
  const invoiceImage = linkedExpense?.image;

  // Init form fields when invoice changes
  useEffect(() => {
    if (invoice) {
      setInvoiceNumber(invoice.inoviceNumber || "");
      setInvoiceDate(invoice.inoviceDate || "");
      setVendorName(invoice.vendorName || "");
      setBusinessNumber(invoice.businessNumber || "");
      setBusinessType(invoice.businessType || "");
      setSubTotal(invoice.subTotal?.toString() || "");
      setTaxRate(invoice.taxRate?.toString() || "");
      setTaxAmount(invoice.taxAmount?.toString() || "");
      setTotalAmount(invoice.totalAmount?.toString() || "");
    }
  }, [
    invoice,
    invoice?.inoviceNumber,
    invoice?.totalAmount,
    invoice?.taxAmount,
    invoice?.subTotal,
  ]);

  // Auto-calculate subtotal + taxAmount when totalAmount + taxRate change (edit mode only)
  useEffect(() => {
    if (isEditing && totalAmount && taxRate) {
      const total = parseFloat(totalAmount) || 0;
      const rate = parseFloat(taxRate) || 0;
      if (total > 0 && rate > 0) {
        const calcSubtotal = total / (1 + rate / 100);
        const calcTax = total - calcSubtotal;
        setSubTotal(calcSubtotal.toFixed(2));
        setTaxAmount(calcTax.toFixed(2));
      }
    }
  }, [totalAmount, taxRate, isEditing]);

  if (!visible || !invoice) return null;

  // ── API ───────────────────────────────────────────────────────────────────
  const fetchAllData = async () => {
    if (!userData) return;
    try {
      const response = await axios.post(`${MyDomain}Employee/GetAllData`, {
        UserId: userData.id,
      });
      const data = response.data || {};
      setAllData(data);
      localStorage.setItem("allData", JSON.stringify(data));
    } catch (err) {
      console.error("❌ Error fetching data:", err);
    }
  };

  const handleUpdateInvoice = async () => {
    if (!invoiceNumber.trim()) {
      alert("Please enter invoice number");
      return;
    }
    if (!vendorName.trim()) {
      alert("Please enter vendor name");
      return;
    }
    if (!subTotal || parseFloat(subTotal) <= 0) {
      alert("Please enter a valid subtotal");
      return;
    }
    if (!linkedExpense) {
      alert("Linked expense not found");
      return;
    }

    setIsSaving(true);
    try {
      const requestData = {
        Id: linkedExpense.id,
        Title: linkedExpense.title || "",
        Note: linkedExpense.note || "",
        Date: linkedExpense.date || "",
        Type: linkedExpense.type || 1,
        Price: parseInt(parseFloat(totalAmount)) || 0,
        WhoMake: linkedExpense.whoMake || userData?.id,
        PlaceId: linkedExpense.placeId || null,
        InvoiceData: {
          InvoiceNumber: invoiceNumber || "",
          InvoiceDate: invoiceDate || "",
          VendorName: vendorName || "",
          BusinessType: businessType || "",
          BusinessNumber: businessNumber || "",
          SubTotal: subTotal || "",
          TaxAmount: taxAmount || "",
          TaxRate: taxRate || "",
          TotalAmount: totalAmount || "",
        },
      };

      const response = await axios.put(
        `${MyDomain}Expenses/UpdateExpenses`,
        requestData,
        { headers: { "Content-Type": "application/json" } },
      );

      if (response.status === 200) {
        // Update localStorage
        const currentData = JSON.parse(localStorage.getItem("allData") || "{}");
        const updated = {
          ...currentData,
          myInvoices: (currentData.myInvoices || []).map((inv) =>
            inv.id === invoice.id
              ? {
                  ...inv,
                  inoviceNumber: invoiceNumber,
                  inoviceDate: invoiceDate,
                  vendorName,
                  businessType,
                  businessNumber,
                  subTotal: parseFloat(subTotal),
                  taxAmount: parseFloat(taxAmount),
                  taxRate: parseFloat(taxRate),
                  totalAmount: parseFloat(totalAmount),
                }
              : inv,
          ),
          myExpenses: (currentData.myExpenses || []).map((exp) =>
            exp.id === linkedExpense.id
              ? { ...exp, price: parseInt(parseFloat(totalAmount)) }
              : exp,
          ),
        };
        localStorage.setItem("allData", JSON.stringify(updated));
        setAllData(updated);

        setIsEditing(false);
        alert("Invoice updated successfully!");
      }
    } catch (error) {
      console.error(
        "❌ Error updating invoice:",
        error.response?.data || error.message,
      );
      alert(error.response?.data?.message || "Failed to update invoice");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setInvoiceNumber(invoice.inoviceNumber || "");
    setInvoiceDate(invoice.inoviceDate || "");
    setVendorName(invoice.vendorName || "");
    setBusinessNumber(invoice.businessNumber || "");
    setBusinessType(invoice.businessType || "");
    setSubTotal(invoice.subTotal?.toString() || "");
    setTaxRate(invoice.taxRate?.toString() || "");
    setTaxAmount(invoice.taxAmount?.toString() || "");
    setTotalAmount(invoice.totalAmount?.toString() || "");
    setIsEditing(false);
  };

  // ── Export ────────────────────────────────────────────────────────────────
  const generateInvoiceHTML = () => `
    <!DOCTYPE html><html><head><meta charset="utf-8">
    <style>
      body{font-family:Arial,sans-serif;padding:40px}
      .header{text-align:center;margin-bottom:40px;border-bottom:3px solid #3B82F6;padding-bottom:20px}
      .header h1{color:#3B82F6;margin:0;font-size:32px}
      .invoice-info{display:flex;justify-content:space-between;margin-bottom:30px}
      .info-box{background:#F9FAFB;padding:15px;border-radius:8px;flex:1;margin:0 10px}
      .info-label{color:#6B7280;font-size:12px;margin-bottom:5px}
      .info-value{color:#111827;font-size:16px;font-weight:bold}
      .section{margin-bottom:30px}
      .section-title{color:#111827;font-size:18px;font-weight:bold;margin-bottom:15px;border-bottom:2px solid #E5E7EB;padding-bottom:10px}
      .vendor-info{background:#fff;border:1px solid #E5E7EB;border-radius:8px;padding:20px}
      .vendor-row{display:flex;margin-bottom:10px}
      .vendor-label{color:#6B7280;font-size:14px;width:150px}
      .vendor-value{color:#111827;font-size:14px;font-weight:600}
      .financial-table{background:#F9FAFB;border-radius:8px;padding:20px}
      .financial-row{display:flex;justify-content:space-between;padding:10px 0}
      .financial-label{color:#6B7280;font-size:14px}
      .financial-value{color:#111827;font-size:14px;font-weight:600}
      .divider{height:1px;background:#E5E7EB;margin:15px 0}
      .total-row{display:flex;justify-content:space-between;padding:15px 0}
      .total-label{color:#111827;font-size:18px;font-weight:bold}
      .total-value{color:#10B981;font-size:20px;font-weight:bold}
      .footer{text-align:center;margin-top:50px;padding-top:20px;border-top:1px solid #E5E7EB;color:#6B7280;font-size:12px}
    </style></head><body>
    <div class="header"><h1>Invoice</h1></div>
    <div class="invoice-info">
      <div class="info-box"><div class="info-label">Invoice Number</div><div class="info-value">${invoiceNumber}</div></div>
      <div class="info-box"><div class="info-label">Date</div><div class="info-value">${invoiceDate}</div></div>
    </div>
    <div class="section">
      <div class="section-title">Vendor Information</div>
      <div class="vendor-info">
        <div class="vendor-row"><div class="vendor-label">Vendor Name:</div><div class="vendor-value">${vendorName}</div></div>
        <div class="vendor-row"><div class="vendor-label">Business Number:</div><div class="vendor-value">${businessNumber}</div></div>
        <div class="vendor-row"><div class="vendor-label">Business Type:</div><div class="vendor-value">${businessType}</div></div>
      </div>
    </div>
    <div class="section">
      <div class="section-title">Financial Breakdown</div>
      <div class="financial-table">
        <div class="financial-row"><div class="financial-label">Subtotal</div><div class="financial-value">₪${parseFloat(subTotal || 0).toLocaleString()}</div></div>
        <div class="financial-row"><div class="financial-label">Tax (${taxRate}%)</div><div class="financial-value">₪${parseFloat(taxAmount || 0).toLocaleString()}</div></div>
        <div class="divider"></div>
        <div class="total-row"><div class="total-label">Total Amount</div><div class="total-value">₪${parseFloat(totalAmount || 0).toLocaleString()}</div></div>
      </div>
    </div>
    <div class="footer">Generated on ${new Date().toLocaleDateString()}</div>
    </body></html>`;

  const exportToPDF = () => {
    setShowExportMenu(false);
    setIsExporting(true);
    try {
      const html = generateInvoiceHTML();
      const win = window.open("", "_blank");
      win.document.write(html);
      win.document.close();
      setTimeout(() => {
        win.print();
        setIsExporting(false);
      }, 500);
    } catch {
      alert("Failed to export PDF");
      setIsExporting(false);
    }
  };

  const exportToCSV = () => {
    setShowExportMenu(false);
    setIsExporting(true);
    try {
      const rows = [
        ["Invoice", ""],
        ["Invoice Number", invoiceNumber],
        ["Invoice Date", invoiceDate],
        ["", ""],
        ["Vendor Information", ""],
        ["Vendor Name", vendorName],
        ["Business Number", businessNumber],
        ["Business Type", businessType],
        ["", ""],
        ["Financial Breakdown", ""],
        ["Subtotal", `₪${parseFloat(subTotal || 0).toFixed(2)}`],
        [`Tax (${taxRate}%)`, `₪${parseFloat(taxAmount || 0).toFixed(2)}`],
        ["Total Amount", `₪${parseFloat(totalAmount || 0).toFixed(2)}`],
      ];

      const csvContent =
        "data:text/csv;charset=utf-8," +
        rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");

      const link = document.createElement("a");
      link.setAttribute("href", encodeURI(csvContent));
      link.setAttribute(
        "download",
        `Invoice_${invoiceNumber.replace(/\//g, "_")}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      alert("Failed to export CSV");
    } finally {
      setIsExporting(false);
    }
  };

  // ── Reusable editable field ───────────────────────────────────────────────
  const Field = ({
    label,
    value,
    onChange,
    placeholder = "",
    type = "text",
    highlight = false,
  }) => (
    <div className="space-y-1">
      <p className="text-xs text-gray-500">{label}</p>
      {isEditing ? (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full bg-transparent outline-none border-b-2 border-blue-400 pb-1 font-semibold text-sm text-gray-900 ${
            highlight ? "text-green-500 text-base" : ""
          }`}
        />
      ) : (
        <p
          className={`font-semibold ${highlight ? "text-lg text-green-500" : "text-sm text-gray-900"}`}
        >
          {value || "—"}
        </p>
      )}
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
        <div
          className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-2xl flex flex-col animate-slide-up"
          style={{ height: "90vh" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── Header ── */}
          <div className="flex items-center justify-between px-5 py-5 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
                <Receipt className="w-5 h-5 text-blue-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                {isEditing ? t("edit") : t("invoiceDetails")}
              </h2>
            </div>

            <div className="flex items-center gap-2">
              {!isEditing && (
                <>
                  {/* Edit */}
                  <button
                    onClick={() => setIsEditing(true)}
                    className="w-9 h-9 bg-yellow-100 border border-yellow-200 rounded-full flex items-center justify-center hover:bg-yellow-200 transition"
                  >
                    <Edit2 className="w-4 h-4 text-yellow-500" />
                  </button>

                  {/* Export dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setShowExportMenu((p) => !p)}
                      disabled={isExporting}
                      className="w-9 h-9 bg-blue-50 border border-blue-200 rounded-full flex items-center justify-center hover:bg-blue-100 transition"
                    >
                      <Download className="w-4 h-4 text-blue-500" />
                    </button>
                    {showExportMenu && (
                      <div className="absolute right-0 top-11 bg-white rounded-xl shadow-lg border border-gray-100 z-50 min-w-[160px] overflow-hidden">
                        <button
                          onClick={exportToPDF}
                          className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                        >
                          <FileText className="w-4 h-4 text-red-500" />
                          {t("export")} PDF
                        </button>
                        <div className="h-px bg-gray-100" />
                        <button
                          onClick={exportToCSV}
                          className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                        >
                          <FileText className="w-4 h-4 text-green-500" />
                          {t("export")} CSV
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Close */}
              <button
                onClick={onClose}
                className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* ── Scrollable content ── */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
            {/* Invoice image */}
            {invoiceImage && (
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-3">
                  {t("invoices")}
                </h3>
                <button
                  onClick={() => setImageModalVisible(true)}
                  className="relative w-full rounded-xl overflow-hidden border-2 border-blue-400"
                >
                  <img
                    src={invoiceImage}
                    alt="Invoice"
                    className="w-full h-60 object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-black bg-opacity-60 py-3 flex items-center justify-center gap-2">
                    <Maximize2 className="w-5 h-5 text-white" />
                    <span className="text-white text-sm font-semibold">
                      Tap to enlarge
                    </span>
                  </div>
                </button>
              </div>
            )}

            {/* Invoice number + date */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <Field
                  label={t("invoiceNumber")}
                  value={invoiceNumber}
                  onChange={setInvoiceNumber}
                  placeholder="INV-001"
                />
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <Field
                  label={t("invoiceDate")}
                  value={invoiceDate}
                  onChange={setInvoiceDate}
                  placeholder="DD-MM-YYYY"
                />
              </div>
            </div>

            {/* Vendor information */}
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-3">
                {t("vendorName")}
              </h3>
              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
                {[
                  {
                    Icon: Building,
                    label: t("vendorName"),
                    value: vendorName,
                    onChange: setVendorName,
                    placeholder: "Company Name",
                  },
                  {
                    Icon: CreditCard,
                    label: t("businessNumber"),
                    value: businessNumber,
                    onChange: setBusinessNumber,
                    placeholder: "515123456",
                    type: "number",
                  },
                  {
                    Icon: Briefcase,
                    label: "Business Type",
                    value: businessType,
                    onChange: setBusinessType,
                    placeholder: 'חברה בע"מ',
                  },
                ].map(({ Icon, label, value, onChange, placeholder, type }) => (
                  <div key={label} className="flex items-start gap-3 px-4 py-3">
                    <Icon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <Field
                        label={label}
                        value={value}
                        onChange={onChange}
                        placeholder={placeholder}
                        type={type}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Financial breakdown */}
            <div>
              <h3 className="text-base font-bold text-gray-900 mb-3">
                {t("financialBreakdown")}
              </h3>
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-2">
                {/* Subtotal */}
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-500">{t("subTotal")}</span>
                  {isEditing ? (
                    <input
                      type="number"
                      value={subTotal}
                      onChange={(e) => setSubTotal(e.target.value)}
                      placeholder="0.00"
                      className="bg-transparent outline-none border-b-2 border-blue-400 text-sm font-semibold text-gray-900 text-right w-28"
                    />
                  ) : (
                    <span className="text-sm font-semibold text-gray-900">
                      ₪{parseFloat(subTotal || 0).toLocaleString()}
                    </span>
                  )}
                </div>

                {/* Tax rate */}
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-500">{t("taxRate")} (%)</span>
                  {isEditing ? (
                    <input
                      type="number"
                      value={taxRate}
                      onChange={(e) => setTaxRate(e.target.value)}
                      placeholder="0"
                      className="bg-transparent outline-none border-b-2 border-blue-400 text-sm font-semibold text-gray-900 text-right w-20"
                    />
                  ) : (
                    <span className="text-sm font-semibold text-gray-900">
                      {taxRate}%
                    </span>
                  )}
                </div>

                {/* Tax amount */}
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-gray-500">{t("taxAmount")}</span>
                  {isEditing ? (
                    <input
                      type="number"
                      value={taxAmount}
                      onChange={(e) => setTaxAmount(e.target.value)}
                      placeholder="0.00"
                      className="bg-transparent outline-none border-b-2 border-blue-400 text-sm font-semibold text-gray-900 text-right w-28"
                    />
                  ) : (
                    <span className="text-sm font-semibold text-gray-900">
                      ₪{parseFloat(taxAmount || 0).toLocaleString()}
                    </span>
                  )}
                </div>

                {/* Divider */}
                <div className="h-px bg-gray-200 my-1" />

                {/* Total */}
                <div className="flex items-center justify-between py-2">
                  <span className="text-base font-bold text-gray-900">
                    {t("totalAmount")}
                  </span>
                  {isEditing ? (
                    <input
                      type="number"
                      value={totalAmount}
                      onChange={(e) => setTotalAmount(e.target.value)}
                      placeholder="0.00"
                      className="bg-transparent outline-none border-b-2 border-blue-400 text-lg font-bold text-green-500 text-right w-32"
                    />
                  ) : (
                    <span className="text-lg font-bold text-green-500">
                      ₪{parseFloat(totalAmount || 0).toLocaleString()}
                    </span>
                  )}
                </div>

                {/* Edit info note */}
                {isEditing && (
                  <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5 mt-2">
                    <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    <p className="text-xs text-blue-700 leading-snug">
                      Expense price will be updated to the Total Amount value
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
            {isEditing ? (
              <div className="flex gap-3">
                <button
                  onClick={handleCancelEdit}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3.5 rounded-xl transition"
                >
                  {t("cancel")}
                </button>
                <button
                  onClick={handleUpdateInvoice}
                  disabled={isSaving}
                  className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold transition ${
                    isSaving
                      ? "bg-gray-300 text-gray-400 cursor-not-allowed"
                      : "bg-green-500 hover:bg-green-600 text-white"
                  }`}
                >
                  {isSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t("loading")}
                    </>
                  ) : (
                    t("ok")
                  )}
                </button>
              </div>
            ) : (
              <button
                onClick={onClose}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3.5 rounded-xl transition"
              >
                {t("back")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Full-screen image modal ── */}
      {imageModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-95 z-[60] flex items-center justify-center">
          <button
            onClick={() => setImageModalVisible(false)}
            className="absolute top-10 right-5"
          >
            <X className="w-9 h-9 text-white" />
          </button>
          {invoiceImage && (
            <img
              src={invoiceImage}
              alt="Invoice full"
              className="max-w-full max-h-[80vh] object-contain"
            />
          )}
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type="number"] { -moz-appearance: textfield; }
      `}</style>
    </>
  );
}
